import { API_ORIGIN, apiUrl } from "./apiOrigin";
// Riddor.js  (Accident book detail view)
// Opened from the Head Office "Accident book" card. Fetches
// /api/headoffice/accident-book/riddor-grid (org-scoped, Bearer auth) and renders three panels
// by riddorStatus:
//   1. Accidents reported with as yet not known RIDDOR status  (red)
//        pharmacy · date · assigned manager · date of last reminder · days since accident
//   2. Accidents reported to RIDDOR  (amber)
//        pharmacy · accident date · RIDDOR reported · reference number · interval (days)
//   3. Non-RIDDOR reportable accidents  (plain)
//        pharmacy · date
//
// Every row has a hover tooltip with the incident summary and, when clicked,
// opens a popup with the full incident detail and a Print button. Columns are
// sortable; rows default to most-recent-first.

import React, { useState, useEffect, useMemo } from "react";
const API_BASE = apiUrl("");

/* ---------------- Helpers ---------------- */
function fmtDate(d) {
  if (!d) return "—";
  const t = new Date(d);
  if (isNaN(t.getTime())) return "—";
  return t.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}
function SortHeader({
  label,
  col,
  sort,
  setSort
}) {
  const active = sort.col === col;
  const arrow = active ? sort.dir === "asc" ? " ▲" : " ▼" : "";
  return <th onClick={() => setSort(s => ({
    col,
    dir: s.col === col && s.dir === "asc" ? "desc" : "asc"
  }))} style={{
    textAlign: "left",
    padding: "10px 12px",
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.4,
    color: "#5D5D5D",
    borderBottom: "2px solid rgba(0,0,0,0.12)",
    whiteSpace: "nowrap",
    cursor: "pointer",
    userSelect: "none"
  }} title="Click to sort">
      {label}{arrow}
    </th>;
}
const DATE_COLS = new Set(["accidentDate", "riddorReportedAt", "lastReminderDate", "reviewDate"]);
const NUM_COLS = new Set(["daysSinceAccident", "intervalDays", "reminderCount"]);
function sortRows(rows, sort) {
  const {
    col,
    dir
  } = sort;
  const mul = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    let av = a[col],
      bv = b[col];
    if (DATE_COLS.has(col)) {
      av = av ? new Date(av).getTime() : 0;
      bv = bv ? new Date(bv).getTime() : 0;
      return (av - bv) * mul;
    }
    if (NUM_COLS.has(col)) {
      av = av == null ? -1 : Number(av);
      bv = bv == null ? -1 : Number(bv);
      return (av - bv) * mul;
    }
    av = (av ?? "").toString().toLowerCase();
    bv = (bv ?? "").toString().toLowerCase();
    return av < bv ? -1 * mul : av > bv ? 1 * mul : 0;
  });
}

/* ---------------- Incident detail popup ---------------- */
function IncidentPopup({
  incident,
  onClose
}) {
  const printIt = () => {
    const w = window.open("", "_blank", "width=720,height=800");
    if (!w) return;
    const row = (k, v) => `<tr><td style="padding:6px 10px;font-weight:600;color:#434343;background:#FAFAFA;vertical-align:top;white-space:nowrap">${k}</td><td style="padding:6px 10px">${(v ?? "—") || "—"}</td></tr>`;
    const statusLabel = {
      NOT_YET_KNOWN: "RIDDOR status not yet known",
      REPORTABLE: "RIDDOR reportable",
      NOT_REPORTABLE: "Non-RIDDOR reportable"
    }[incident.riddorStatus] || incident.riddorStatus || "—";
    w.document.write(`
      <html><head><title>Incident report — ${incident.pharmacyName}</title></head>
      <body style="font-family:Arial,Helvetica,sans-serif;color:#262626;padding:24px;max-width:680px;margin:0 auto">
        <h1 style="font-size:20px;margin:0 0 4px">Accident / incident report</h1>
        <div style="font-size:13px;color:#5D5D5D;margin-bottom:16px">${incident.pharmacyName}</div>
        <table style="width:100%;border-collapse:collapse;font-size:13px;border:1px solid #F0F0F0">
          ${row("Pharmacy", incident.pharmacyName)}
          ${row("RIDDOR status", statusLabel)}
          ${row("Date of accident", fmtDate(incident.accidentDate))}
          ${row("Location", incident.accidentLocation)}
          ${row("Detail", incident.accidentDetail)}
          ${row("Reported by", incident.reportedByName)}
          ${row("Reporter occupation", incident.reportedByOccupation)}
          ${row("Reporter contact", incident.reportedByContactNo)}
          ${row("Reporter email", incident.reportedByContactEmail)}
          ${row("Person involved", incident.patientName)}
          ${row("Person occupation", incident.patientOccupation)}
          ${row("RIDDOR reference", incident.riddorReferenceNumber)}
          ${row("RIDDOR reported", incident.riddorReportedAt ? fmtDate(incident.riddorReportedAt) : "")}
          ${row("Assigned manager", incident.managerName)}
          ${row("Reviewed", incident.reviewDate ? fmtDate(incident.reviewDate) : "Not yet reviewed")}
          ${row("Comments", incident.comments)}
        </table>
        <div style="font-size:11px;color:#8C8C8C;margin-top:20px">Printed ${new Date().toLocaleString("en-GB")}</div>
      </body></html>
    `);
    w.document.close();
    w.focus();
    w.print();
  };
  const Field = ({
    label,
    children
  }) => <div style={{
    marginBottom: 12
  }}>
      <div style={{
      fontSize: 12,
      color: "#5D5D5D",
      marginBottom: 2
    }}>{label}</div>
      <div style={{
      fontSize: 14,
      color: "#262626"
    }}>{children}</div>
    </div>;
  const statusLabel = {
    NOT_YET_KNOWN: "RIDDOR status not yet known",
    REPORTABLE: "RIDDOR reportable",
    NOT_REPORTABLE: "Non-RIDDOR reportable"
  }[incident.riddorStatus] || incident.riddorStatus || "—";
  return <div onClick={onClose} style={{
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000
  }}>
      <div onClick={e => e.stopPropagation()} style={{
      width: "min(640px, 95vw)",
      maxHeight: "88vh",
      background: "#fff",
      borderRadius: 8,
      display: "flex",
      flexDirection: "column",
      boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
    }}>
        <div style={{
        padding: "14px 18px",
        borderBottom: "1px solid #eee",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
          <div>
            <div style={{
            fontWeight: 700,
            fontSize: 16
          }}>Accident / incident report</div>
            <div style={{
            fontSize: 12,
            color: "#5D5D5D"
          }}>{incident.pharmacyName} · {fmtDate(incident.accidentDate)}</div>
          </div>
          <button onClick={onClose} style={{
          border: "1px solid #D9D9D9",
          background: "#fff",
          borderRadius: 4,
          width: 30,
          height: 30,
          cursor: "pointer",
          fontSize: 16,
          lineHeight: 1
        }}>×</button>
        </div>

        <div style={{
        padding: 18,
        overflow: "auto",
        flex: 1
      }}>
          <Field label="RIDDOR status">{statusLabel}</Field>
          <Field label="Location">{incident.accidentLocation || "—"}</Field>
          <Field label="Detail">{incident.accidentDetail || "—"}</Field>
          <Field label="Reported by">{incident.reportedByName || "—"}{incident.reportedByOccupation ? ` · ${incident.reportedByOccupation}` : ""}</Field>
          <Field label="Reporter contact">{[incident.reportedByContactNo, incident.reportedByContactEmail].filter(Boolean).join(" · ") || "—"}</Field>
          <Field label="Person involved">{incident.patientName || "—"}{incident.patientOccupation ? ` · ${incident.patientOccupation}` : ""}</Field>
          {incident.riddorReferenceNumber ? <Field label="RIDDOR reference">{incident.riddorReferenceNumber}</Field> : null}
          {incident.riddorReportedAt ? <Field label="RIDDOR reported">{fmtDate(incident.riddorReportedAt)}</Field> : null}
          {incident.managerName ? <Field label="Assigned manager">{incident.managerName}{incident.managerEmail ? ` (${incident.managerEmail})` : ""}</Field> : null}
          <Field label="Reviewed">{incident.reviewDate ? fmtDate(incident.reviewDate) : <span style={{
            color: "#CF1322"
          }}>Not yet reviewed</span>}</Field>
          <Field label="Comments">{incident.comments || "—"}</Field>
        </div>

        <div style={{
        padding: "10px 18px",
        borderTop: "1px solid #eee",
        display: "flex",
        justifyContent: "flex-end",
        gap: 8
      }}>
          <button onClick={onClose} style={{
          fontSize: 12,
          padding: "6px 14px",
          border: "1px solid #8C8C8C",
          borderRadius: 4,
          background: "#F5F5F5",
          cursor: "pointer"
        }}>Close</button>
          <button onClick={printIt} style={{
          fontSize: 12,
          padding: "6px 16px",
          border: "1px solid #262626",
          borderRadius: 4,
          background: "#262626",
          color: "#fff",
          fontWeight: 500,
          cursor: "pointer"
        }}>Print</button>
        </div>
      </div>
    </div>;
}

/* ---------------- A single status panel ---------------- */
function StatusGrid({
  title,
  subtitle,
  rows,
  columns,
  borderColor,
  headerBg,
  rowBg,
  onRowClick,
  defaultSort
}) {
  const [sort, setSort] = useState(defaultSort || {
    col: "accidentDate",
    dir: "desc"
  });
  const sorted = useMemo(() => sortRows(rows, sort), [rows, sort]);
  const td = {
    padding: "9px 12px",
    fontSize: 13,
    borderBottom: "1px solid rgba(0,0,0,0.06)"
  };
  return <div style={{
    marginBottom: 28
  }}>
      <h3 style={{
      fontSize: 15,
      margin: "0 0 2px"
    }}>{title} <span style={{
        fontWeight: 400,
        color: "#5D5D5D"
      }}>({rows.length})</span></h3>
      {subtitle && <div style={{
      fontSize: 12,
      color: "#5D5D5D",
      marginBottom: 8
    }}>{subtitle}</div>}
      <div style={{
      border: `1px solid ${borderColor}`,
      borderRadius: 8,
      background: rowBg || "#fff",
      maxHeight: 320,
      overflowY: "auto"
    }}>
        <table style={{
        width: "100%",
        borderCollapse: "collapse"
      }}>
          <thead style={{
          position: "sticky",
          top: 0,
          background: headerBg || "#FAFAFA",
          zIndex: 1
        }}>
            <tr>
              {columns.map(c => <SortHeader key={c.key} label={c.label} col={c.key} sort={sort} setSort={setSort} />)}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? <tr><td colSpan={columns.length} style={{
              ...td,
              textAlign: "center",
              color: "#5D5D5D",
              padding: 20
            }}>None.</td></tr> : sorted.map(r => <tr key={r.entryId} onClick={() => onRowClick(r)} title={`${r.pharmacyName} — ${r.accidentDetail || ""}`} style={{
            cursor: "pointer"
          }} onMouseEnter={e => e.currentTarget.style.filter = "brightness(0.96)"} onMouseLeave={e => e.currentTarget.style.filter = "none"}>
                {columns.map(c => <td key={c.key} style={{
              ...td,
              ...(c.cellStyle || {})
            }}>{c.render ? c.render(r) : r[c.key] ?? "—"}</td>)}
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}

/* ---------------- Main view ---------------- */
export default function Riddor() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState({
    notYetKnown: [],
    reportable: [],
    notReportable: []
  });
  const [popup, setPopup] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("authToken");
        const org = localStorage.getItem("organisationName") || "";
        const qs = org ? `?organisationName=${encodeURIComponent(org)}` : "";
        const res = await fetch(`${API_BASE}/api/headoffice/accident-book/riddor-grid${qs}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData({
          notYetKnown: json.notYetKnown || [],
          reportable: json.reportable || [],
          notReportable: json.notReportable || []
        });
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return <div style={{
    padding: "16px 20px"
  }}>
      <h2 style={{
      margin: "0 0 8px",
      fontSize: 20
    }}>Accident book</h2>

      {/* Intro / criteria */}
      <div style={{
      fontSize: 13,
      color: "#434343",
      lineHeight: 1.6,
      marginBottom: 20,
      maxWidth: 900
    }}>
        RIDDOR — the Reporting of Injuries, Diseases and Dangerous Occurrences Regulations 2013 — places a legal
        duty on employers to report certain serious workplace incidents to the Health and Safety Executive (HSE).
        In a pharmacy setting a report is generally required when there is: the death of any person; a "specified
        injury" to a worker (such as a fracture other than to fingers/thumbs/toes, amputation, loss of sight,
        crush injury, serious burn, or loss of consciousness); an injury to a worker that leads to more than seven
        consecutive days' incapacity for work; an injury to a member of the public that results in them being taken
        directly to hospital for treatment; certain occupational diseases; or a dangerous occurrence (a near-miss
        with the potential to cause serious harm). Reports must usually be made without delay, and over-7-day
        injuries within 15 days of the accident. Accidents whose status is <strong>not yet known</strong> are shown
        first, in red, as they are the most urgent to resolve.
      </div>

      {error && <div style={{
      color: "#CF1322",
      padding: 12,
      background: "#FFF1F0",
      borderRadius: 4,
      marginBottom: 16
    }}>
          Error: {error}
        </div>}
      {loading && <div style={{
      color: "#5D5D5D",
      padding: 24
    }}>Loading…</div>}

      {!loading && !error && <>
          {/* 1. NOT_YET_KNOWN — red */}
          <StatusGrid title="Accidents reported with as yet not known RIDDOR status" subtitle="Awaiting a reportable / not-reportable decision. The assigned manager is being chased — resolve as a priority." rows={data.notYetKnown} borderColor="#CF1322" headerBg="#FFCCC7" rowBg="#FFF1F0" defaultSort={{
        col: "accidentDate",
        dir: "desc"
      }} onRowClick={setPopup} columns={[{
        key: "pharmacyName",
        label: "Pharmacy",
        render: r => <strong>{r.pharmacyName}</strong>,
        cellStyle: {
          color: "#820014"
        }
      }, {
        key: "accidentDate",
        label: "Date",
        render: r => fmtDate(r.accidentDate),
        cellStyle: {
          color: "#820014"
        }
      }, {
        key: "managerName",
        label: "Assigned manager",
        render: r => r.managerName || "—",
        cellStyle: {
          color: "#820014"
        }
      }, {
        key: "lastReminderDate",
        label: "Last reminder",
        render: r => r.lastReminderDate ? fmtDate(r.lastReminderDate) : <span style={{
          fontStyle: "italic"
        }}>none sent</span>,
        cellStyle: {
          color: "#820014"
        }
      }, {
        key: "daysSinceAccident",
        label: "Days since accident",
        render: r => r.daysSinceAccident ?? "—",
        cellStyle: {
          color: "#820014",
          fontWeight: 700
        }
      }]} />

          {/* 2. REPORTABLE — amber */}
          <StatusGrid title="Accidents reported to RIDDOR" rows={data.reportable} borderColor="#FA8C16" headerBg="#FFE58F" rowBg="#FFF7E6" defaultSort={{
        col: "riddorReportedAt",
        dir: "desc"
      }} onRowClick={setPopup} columns={[{
        key: "pharmacyName",
        label: "Pharmacy",
        render: r => <strong>{r.pharmacyName}</strong>,
        cellStyle: {
          color: "#AD4E00"
        }
      }, {
        key: "accidentDate",
        label: "Date of accident",
        render: r => fmtDate(r.accidentDate),
        cellStyle: {
          color: "#AD4E00"
        }
      }, {
        key: "riddorReportedAt",
        label: "Date RIDDOR reported",
        render: r => fmtDate(r.riddorReportedAt),
        cellStyle: {
          color: "#AD4E00"
        }
      }, {
        key: "riddorReferenceNumber",
        label: "Reference number",
        render: r => <span style={{
          fontFamily: "monospace"
        }}>{r.riddorReferenceNumber || "—"}</span>,
        cellStyle: {
          color: "#AD4E00"
        }
      }, {
        key: "intervalDays",
        label: "Interval between accident/report",
        render: r => r.intervalDays == null ? "—" : `${r.intervalDays} day${r.intervalDays === 1 ? "" : "s"}`,
        cellStyle: {
          color: "#AD4E00",
          fontWeight: 700
        }
      }]} />

          {/* 3. NOT_REPORTABLE — plain */}
          <StatusGrid title="Non-RIDDOR reportable accidents" rows={data.notReportable} borderColor="#F0F0F0" headerBg="#FAFAFA" rowBg="#fff" defaultSort={{
        col: "accidentDate",
        dir: "desc"
      }} onRowClick={setPopup} columns={[{
        key: "pharmacyName",
        label: "Pharmacy",
        render: r => <strong>{r.pharmacyName}</strong>
      }, {
        key: "accidentDate",
        label: "Date",
        render: r => fmtDate(r.accidentDate)
      }]} />
        </>}

      {popup && <IncidentPopup incident={popup} onClose={() => setPopup(null)} />}
    </div>;
}
